/**
 * /user-story-template — SEO template page for "user story template (word)"
 * + agile spec-artifact long-tail (~103 traffic, vol 700, strong fit).
 * Hands over the As-a/I-want/So-that template + INVEST checklist + acceptance
 * criteria link, then the Draftlize turn: a story is stale the moment the
 * decision under it moves, and nothing links the story to that decision.
 *
 * English-only + inline. Chinese variant in UserStoryTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function UserStoryTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/user-story-template"
      documentTitle="User Story Template (As-a, I-want, So-that) · Draftlize"
      documentDescription="A user story template you can copy — the As-a / I-want / So-that format, the INVEST checklist, and acceptance criteria — plus how to keep a story tied to the decision under it."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · User stories</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A user story template<br/>
            <em>that stays tied to the why.</em>
          </h1>
          <p className="lede">
            You came for a <em>user story template</em> — the As-a / I-want / So-that format, the INVEST checklist, the acceptance-criteria block. It&apos;s below; copy it into Jira, a doc, or wherever stories live. But know the failure mode: a story captures a need in one sentence and then floats free of the product decision that justified it. When that decision changes, the story keeps sitting in the sprint, quietly building the wrong thing. Draftlize gives you the same format — and links each story to the decision it rests on.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">The format. <em>Copy it anywhere.</em></h2>
          </div>
          <p>A user story is one sentence in three parts, plus the criteria that make it testable. This is the whole template — it&apos;s the same shape agile teams have used forever.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'As a [role]', hint: 'Who', body: 'The specific user or persona, not "the user." "As a PM reviewing a roadmap" frames the need; "as a user" frames nothing. The role is what keeps the story honest about who it serves.' },
            { k: 'I want [capability]', hint: 'What', body: 'The thing they want to do, stated as a capability, not a UI. "I want to cite a decision from a spec" — not "I want a dropdown." How it looks is a design decision; the story is about the goal.' },
            { k: 'So that [benefit]', hint: 'Why', body: 'The outcome that makes it worth doing. This is the part teams drop and the most important one — it\'s the test of whether the story is worth building at all, and the first thing to check when scope is questioned.' },
            { k: 'Acceptance criteria', hint: 'Done when', body: 'The conditions that make it shippable, ideally as Given/When/Then. This is where a story becomes testable. See the dedicated acceptance criteria guide for the full pattern.' },
            { k: 'INVEST check', hint: 'Quality bar', body: 'A good story is Independent, Negotiable, Valuable, Estimable, Small, Testable. Run a draft story against these six and the weak ones (too big, not testable, no value) fall out before they reach a sprint.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          Worked example: <em>As a PM reviewing a roadmap, I want to cite a decision from a spec, so that I don&apos;t re-explain it every sprint.</em>
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a story <em>floats free.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The &quot;so that&quot; is a copy, not a link</h3>
            <p>The benefit clause references a product decision — &quot;so that&quot; some goal is served. But in a ticket it&apos;s just text, a restatement of a decision that lives somewhere else. The story and the decision it depends on are two disconnected things that happen to agree, for now.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A decision changes, the story doesn&apos;t</h3>
            <p>The goal behind the story shifts — you re-scope, you reprioritize, you drop a segment. The story is still sitting in the backlog with its original &quot;so that,&quot; now pointing at a reason that no longer holds. Nobody re-reads every story when a decision moves, so the sprint quietly builds to a stale why.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Acceptance criteria drift from the spec</h3>
            <p>The criteria were written against a spec that has since changed. The story says &quot;done when X,&quot; the spec now says Y, and the gap surfaces in review or QA — late, expensive, and avoidable if the criteria had been linked to the spec instead of copied from it.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same story, <em>wired in.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each story is a card linked to its why</h3>
            <p>In Draftlize a story isn&apos;t a standalone ticket — it&apos;s a card whose &quot;so that&quot; links to the actual decision card it serves. The benefit isn&apos;t restated; it&apos;s referenced. Open the story and you can walk straight to the decision that justifies it.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The decision changes, the story flags stale</h3>
            <p>Because the link is real, changing the decision behind a story turns the story stale automatically — the way a build system invalidates everything downstream of a changed file. You see which stories just lost their reason, before a sprint builds them anyway.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent reads the story and its context</h3>
            <p>When Claude Code or Cursor picks up a story over MCP, it reads the linked decision and acceptance criteria too — so it implements against the current intent, not a sentence that lost its meaning three decisions ago.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A user story captures a need in a sentence. It can&apos;t tell you when the decision that made the need real has moved on.
        </blockquote>
        <cite>Keep the format. Let the &quot;so that&quot; link to the decision, not just describe it.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is the standard user story format?', a: 'The classic template is "As a [role], I want [capability], so that [benefit]." The role names who it is for, the capability names the goal (not the UI), and the benefit names why it is worth doing. Acceptance criteria are added to make it testable.' },
            { q: 'What is the difference between a user story and a use case?', a: 'A user story is a short, informal statement of a need from the user\'s perspective, meant to start a conversation. A use case is a more detailed description of the interaction steps between a user and the system. Stories are lightweight and agile; use cases are heavier and more complete.' },
            { q: 'What makes a good user story? (INVEST)', a: 'A good story is Independent, Negotiable, Valuable, Estimable, Small, and Testable. The most common failures are stories that are too big to estimate, have no clear value in the "so that," or are not testable because there are no acceptance criteria.' },
            { q: 'What is the difference between a user story and acceptance criteria?', a: 'The story states the need; the acceptance criteria state the conditions under which it is considered done, usually as Given/When/Then. The story is the why and what at a glance; the criteria are the precise, testable definition of success.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Write stories that keep their why <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Take the template above, or write each story as a card in Draftlize linked to the decision it serves — so when the decision moves, the story flags stale instead of quietly building the wrong thing.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
